/**
 * InlineRowGrid — the core authoring primitive: an editable, keyboard-friendly
 * table where each row is a record and each column an editable cell. Add / edit
 * / delete / reorder rows in place; commit is up to the caller (it owns the
 * rows and receives every change via onChange). Built on the kit `.table-wrap`
 * / `.dt` / `.btn` classes so it looks in-family with the read tables.
 *
 * Generic over the row type; columns declare how to read/write each field. The
 * W2–W6 design screens (hole plan, casing, program, cost lines, attributes,
 * trajectory) all render one of these over their dataset.
 */
import { useCallback, type ReactNode } from "react";

export interface GridColumn<Row> {
  /** Stable key (also the header i18n slot the caller passes as `label`). */
  key: string;
  label: ReactNode;
  /** Render the cell's current value as an editable control. */
  render: (row: Row, update: (patch: Partial<Row>) => void, rowIndex: number) => ReactNode;
  /** Optional fixed width / alignment hint. */
  width?: number | string;
  align?: "left" | "right" | "center";
  /** Mark a column as numeric (right-aligned tabular by default). */
  numeric?: boolean;
}

export interface InlineRowGridProps<Row> {
  rows: Row[];
  columns: GridColumn<Row>[];
  onChange: (rows: Row[]) => void;
  /** Build a fresh empty row when the user clicks "+ Add row". */
  makeEmptyRow?: (index: number) => Row;
  addLabel?: string;
  /** Read-only mode (e.g. a locked/published plan) hides edit affordances. */
  readOnly?: boolean;
  /** Optional caption / total row rendered under the body. */
  footer?: ReactNode;
  emptyLabel?: string;
}

export function InlineRowGrid<Row>({
  rows,
  columns,
  onChange,
  makeEmptyRow,
  addLabel = "+ Add row",
  readOnly = false,
  footer,
  emptyLabel = "—",
}: InlineRowGridProps<Row>) {
  const updateRow = useCallback(
    (index: number, patch: Partial<Row>) => {
      const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
      onChange(next);
    },
    [rows, onChange],
  );

  const removeRow = useCallback(
    (index: number) => onChange(rows.filter((_, i) => i !== index)),
    [rows, onChange],
  );

  const moveRow = useCallback(
    (index: number, dir: -1 | 1) => {
      const target = index + dir;
      if (target < 0 || target >= rows.length) return;
      const next = [...rows];
      const [row] = next.splice(index, 1);
      if (row === undefined) return;
      next.splice(target, 0, row);
      onChange(next);
    },
    [rows, onChange],
  );

  const addRow = useCallback(() => {
    if (!makeEmptyRow) return;
    onChange([...rows, makeEmptyRow(rows.length)]);
  }, [rows, onChange, makeEmptyRow]);

  return (
    <div className="inline-row-grid">
      <div className="table-wrap">
        <table className="dt">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{
                    width: c.width,
                    textAlign: c.align ?? (c.numeric ? "right" : "left"),
                  }}
                >
                  {c.label}
                </th>
              ))}
              {!readOnly && <th style={{ width: 92 }} aria-label="row actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (readOnly ? 0 : 1)} className="dim" style={{ textAlign: "center", padding: 20 }}>
                  {emptyLabel}
                </td>
              </tr>
            )}
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={c.numeric ? "tab-nums" : undefined}
                    style={{ textAlign: c.align ?? (c.numeric ? "right" : "left") }}
                  >
                    {c.render(row, (patch) => updateRow(i, patch), i)}
                  </td>
                ))}
                {!readOnly && (
                  <td className="row-tools">
                    <button type="button" className="icon-btn" title="Move up" aria-label="Move up" onClick={() => moveRow(i, -1)} disabled={i === 0}>
                      ↑
                    </button>
                    <button type="button" className="icon-btn" title="Move down" aria-label="Move down" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1}>
                      ↓
                    </button>
                    <button type="button" className="icon-btn danger" title="Delete row" aria-label="Delete row" onClick={() => removeRow(i)}>
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
      {!readOnly && makeEmptyRow && (
        <button type="button" className="btn" style={{ marginTop: 10 }} onClick={addRow}>
          <span className="ic ic-add" aria-hidden="true" />
          {addLabel}
        </button>
      )}
    </div>
  );
}
